import { useEffect, useState } from "react";

// material components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

// components
import Button from "../../common/button";
import TextInput from "../../forms/textInput";

// styles
import styles from "./styles.module.css";

// api
import { updateProfileInfo } from "../../../setup/api/services/profile";
import { uploadFile } from "../../../s3Integration/s3Client";

// types
import { IUserInfoResponse } from "../../../setup/api/services/user/models";
import { BUTTON, FORM, PERSONAL_DATA } from "./constants";
import { getUserId } from "../../../setup/auth/oidc";
import { ALERTMESSAGE } from "../../../enums";
import { EUploadImageType } from "../../../s3Integration/enums";

interface Props {
  profileFullName?: IUserInfoResponse["data"]["profile"]["fullName"];
  photoUrl?: IUserInfoResponse["data"]["profile"]["photoUrl"];
  phoneNumber?: IUserInfoResponse["data"]["phoneNumber"];
}

export enum ALERT {
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  NONE = "NONE",
}

const PersonalData = ({
  profileFullName,
  photoUrl,
  phoneNumber,
}: Props): JSX.Element => {
  const userId = getUserId();

  const [loading, setLoading] = useState<boolean>(false);
  const [imageUploading, setImageUploading] = useState<boolean>(false);
  const [alert, setAlert] = useState<ALERT>(ALERT.NONE);
  const [fullName, setFullName] =
    useState<IUserInfoResponse["data"]["profile"]["fullName"]>("");
  const [imageUrl, setImageUrl] = useState<any>("");
  const [mobileNumber, setMobileNumber] =
    useState<IUserInfoResponse["data"]["phoneNumber"]>("");

  useEffect(() => {
    profileFullName && setFullName(profileFullName);
    photoUrl && setImageUrl(photoUrl);
    phoneNumber && setMobileNumber(phoneNumber);
  }, [profileFullName, photoUrl, phoneNumber]);

  useEffect(() => {
    if (alert !== ALERT.NONE) {
      setTimeout(() => {
        setAlert(ALERT.NONE);
      }, 2000);
    }
  }, [alert]);

  const submitEdits = async () => {
    setLoading(true);
    const data = {
      fullName: fullName,
      photoUrl: imageUrl,
      firstName: fullName?.split(" ")?.[0],
      lastName: fullName?.split(" ")?.[1],
    };
    try {
      userId && (await updateProfileInfo(userId, data));
      setAlert(ALERT.SUCCESS);
      setLoading(false);
    } catch (error) {
      setAlert(ALERT.ERROR);
      setLoading(false);
    }
  };

  const handleOnChange = async (e: any) => {
    if (!userId) return;
    setImageUploading(true);
    const image = e?.target?.files[0];
    try {
      setImageUploading(false);
      const url = await uploadFile(image, EUploadImageType.PROFILE, userId);
      setImageUrl(url);
    } catch (error) {
      console.error(e);
      setImageUploading(false);
    }
  };

  return (
    <Box className={styles["container"]}>
      <div>
        <Typography className={styles["header"]}>
          {PERSONAL_DATA.MODIFY}
        </Typography>
        <div className={styles["avatar_wrapper"]}>
          <input
            onChange={handleOnChange}
            className={styles["avatar_top"]}
            type="file"
            name="picture"
            id="picture"
          />
          <Avatar className={styles["avatar"]} src={imageUrl} />
        </div>
        <TextInput
          placeholder={FORM.NAME}
          required={true}
          label={FORM.NAME}
          defaultValue={fullName}
          handleChange={(e) => setFullName(e?.target?.value)}
        />
        <TextInput
          placeholder={FORM.MOBILE_NUMBER}
          label={FORM.NAME}
          dir="ltr"
          defaultValue={mobileNumber}
          disabled
          handleChange={(e) => setMobileNumber(e?.target?.value)}
        />
        <Button disabled={loading || fullName === ""} onClick={submitEdits}>
          {loading ? (
            <CircularProgress color="inherit" />
          ) : (
            `${BUTTON.SAVE_EDIT}`
          )}
        </Button>
      </div>
      {alert !== ALERT.NONE && (
        <Alert severity={alert === ALERT.SUCCESS ? "success" : "error"}>
          {alert === ALERT.SUCCESS ? ALERTMESSAGE.SUCCESS : ALERTMESSAGE.ERROR}
        </Alert>
      )}
    </Box>
  );
};

export default PersonalData;
